/**
 * 准备金证明弹框组件
 */
import './index.less';

import Icon, {
  ArrowRightOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button, displayThousands } from '@bfe/moly';
import { Col, message, Row, Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import orderBy from 'lodash-es/orderBy';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ByModal from '@/components/ByModal';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useI18nContext } from '@/i18n/I18n';
import { postMerkleTreeInfo } from '@/services/por';
import { copyText, downloadFile } from '@/utils/copy';
import { removeTrailingZeros } from '@/utils/sum';
import { isMobile } from '@/utils/ua';

import { IAssetBalanceItem, IReserveInfo } from '../../types';
import copyIcon from './copyIcon';

const { Paragraph } = Typography;

interface IReserveProps {
  visible: boolean;
  downFileUrl: string;
  reserveInfo: IReserveInfo | any;
  onCancel?: () => void;
}
const ReserveDetailModal: FC<IReserveProps> = ({
  visible,
  downFileUrl,
  reserveInfo,
  onCancel,
}) => {
  const [$t, language] = useI18nContext();
  const navigate = useNavigate();
  const { msgError } = useUserInfo();
  const [loading, setLoading] = useState<boolean>(false);
  const [assetBalance, setAssetBalance] = useState<any>([]);
  const [assetType, setAssetType] = useState<string>('');

  useEffect(() => {
    if (reserveInfo?.assetBalance) {
      const assetBalance = orderBy(
        reserveInfo?.assetBalance || [],
        [(asset) => Number(asset.balance), 'coin'],
        ['desc', 'asc'],
      );
      setAssetType(assetBalance.map((item) => item.coin).join(','));
      setAssetBalance(
        assetBalance.map((item) => {
          const balance = item.balance;
          return {
            ...item,
            balance: displayThousands(
              Number(balance) === 0
                ? '0.0000'
                : removeTrailingZeros(`${balance}`),
              8,
              language,
            ),
          };
        }),
      );
    }
  }, [reserveInfo?.assetBalance, language]);

  // 复制到剪切板
  const copyHandle = (text?: string) => {
    text && copyText(text);
  };

  // 渲染label 和 content公共方法
  const renderLabelContent = (
    label: string,
    content: string,
    handle?: 'copy' | 'tip' | '',
    tip?: string,
  ) => {
    return (
      <div className={classNames('reserve-label-content')}>
        <div className={classNames('reserve-label')}>{label}</div>
        <div className={classNames('reserve-content')}>
          {handle !== 'copy' && (
            <span style={{ maxWidth: '100%' }}>{content}&nbsp;&nbsp;</span>
          )}
          {handle && handle === 'copy' && (
            <Paragraph
              copyable={{
                icon: [<Icon key="copy-icon" component={copyIcon} />],
                tooltips: [''],
              }}
            >
              {content}
            </Paragraph>
          )}
          {handle && handle === 'tip' && (
            <Tooltip placement="top" title={tip}>
              <QuestionCircleOutlined className="tooltipBtn" />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  const goOtherPage = (e: any, url: string) => {
    e && e.preventDefault();
    navigate(url);
  };
  // 复制全部数据
  const onCopyBtnClick = () => {
    setLoading(true);
    postMerkleTreeInfo({ auditId: reserveInfo?.auditId, copy: true })
      .then((res: any) => {
        setLoading(false);
        message.success($t('Address Copied'));
        copyHandle(JSON.stringify(res?.result)); // 复制结果到剪切板
      })
      .catch((err: any) => {
        setLoading(false);
        if (err) {
          msgError(err);
        } else {
          message.error('network error! try again later');
        }
      });
  };

  return (
    <ByModal
      visible={visible}
      width={780}
      title={`${$t('reserveProof')} - ${reserveInfo?.auditId}`}
      onCancel={() => onCancel && onCancel()}
      modalProps={{ className: isMobile() ? 'reserve-modal-h5' : '' }}
    >
      <div className="reserve-modal-content">
        <Row>
          <Col span={isMobile() ? 10 : 6}>
            {renderLabelContent($t('auditNumber'), reserveInfo?.auditId)}
          </Col>
          <Col span={isMobile() ? 14 : 18}>
            {renderLabelContent($t('Date'), reserveInfo?.date)}
          </Col>
        </Row>
        {renderLabelContent(
          $t('MERKLE HASH'),
          reserveInfo?.merkleHash || 'N/A',
          reserveInfo?.merkleHash ? 'copy' : '',
        )}
        {renderLabelContent(
          $t('MERKLE LEAF'),
          reserveInfo?.merkleLeaf || 'N/A',
          reserveInfo?.merkleHash ? 'copy' : '',
        )}
        {renderLabelContent(
          $t('Asset Type'),
          assetType || 'N/A',
          assetType?.indexOf('Stablescoin') > -1 ? 'tip' : '',
          assetType?.indexOf('Stablescoin') > -1
            ? 'Stablescoin=USDT+USDC+BUSD'
            : 'N/A',
        )}

        <div className={classNames('asset-table')}>
          <Row>
            <Col span={!isMobile() ? 12 : 24} className="asset-item">
              <Row>
                <Col span={8}>
                  <span className="chain-span">{$t('Assets')}</span>
                </Col>
                <Col span={16}>
                  <span className="chain-span">{$t('Insurance.Balance')}</span>
                </Col>
              </Row>
            </Col>
            {!isMobile() ? (
              <Col span={12} className="asset-item">
                <Row>
                  <Col span={8}>
                    <span className="chain-span">{$t('Assets')}</span>
                  </Col>
                  <Col span={16}>
                    <span className="chain-span">
                      {$t('Insurance.Balance')}
                    </span>
                  </Col>
                </Row>
              </Col>
            ) : null}
          </Row>
          <Row className="asset-list">
            {assetBalance?.length ? (
              assetBalance?.map((item: IAssetBalanceItem) => {
                return (
                  <Col
                    span={!isMobile() ? 12 : 24}
                    className="asset-item"
                    key={item.coin}
                  >
                    <Row>
                      <Col span={8}>
                        <span className="coin-span">{item.coin}</span>
                        <span className="chain-span">{item?.chain}</span>
                      </Col>
                      <Col span={16}>{item.balance}</Col>
                    </Row>
                  </Col>
                );
              })
            ) : (
              <>
                <Col span={!isMobile() ? 12 : 24} className="asset-item">
                  <Row>
                    <Col span={8}>
                      <span className="coin-span">N/A</span>
                    </Col>
                    <Col span={16}>N/A</Col>
                  </Row>
                </Col>
                {!isMobile() ? (
                  <Col span={12} className="asset-item">
                    <Row>
                      <Col span={8}>
                        <span className="coin-span">N/A</span>
                      </Col>
                      <Col span={16}>N/A</Col>
                    </Row>
                  </Col>
                ) : null}
              </>
            )}
          </Row>
        </div>

        {reserveInfo?.assetBalance ? (
          <>
            <div className={classNames('tip-content')}>
              {$t('balanceSnapshot')}
            </div>
            <div className={classNames('tip-content')}>
              {$t('balanceErrTip')}
            </div>

            <div className="linkBtn">
              <a
                href={'/app/user/proof-of-reserve'}
                onClick={(e) => goOtherPage(e, '/app/user/proof-of-reserve')}
              >
                {$t('learnMore')}&nbsp;&nbsp;
                <ArrowRightOutlined />
              </a>
            </div>

            <div className={classNames('title-18')}>{$t('HowSelfVerify')}</div>
            <div className={classNames('tip-content')}>
              {$t('readInstructions')}
            </div>
            <div className="linkBtn font-weight-500">
              <a
                href={`/${language}/help-center/bybitHC_Article?id=000001874`}
                // onClick={(e) =>
                //   goOtherPage(
                //     e,
                //     '/en-US/help-center/bybitHC_Article?id=000001874&language=en_US',
                //   )
                // }
              >
                {$t('howVerifyWalletAddress')}
              </a>
            </div>
            <div className="linkBtn font-weight-500">
              <a
                href={`/${language}/help-center/bybitHC_Article?id=000001873`}
                // onClick={(e) =>
                //   goOtherPage(
                //     e,
                //     '/en-US/help-center/bybitHC_Article?id=000001873&language=en_US',
                //   )
                // }
              >
                {$t('howCheckMerkle')}
              </a>
            </div>
            <div className={classNames('btn-contianer')}>
              <Button
                variant="primary"
                className="bybit-btn-primary min-width-196"
                loading={loading}
                onClick={onCopyBtnClick}
              >
                {$t('copyData')}
              </Button>
              <Button
                className="min-width-196 download-btn"
                variant="secondary"
                onClick={() => {
                  downloadFile({
                    href: downFileUrl,
                  });
                }}
              >
                {$t('downloadProof')}
              </Button>
            </div>
          </>
        ) : (
          <div>{$t('auditDetailNoneTip')}</div>
        )}
      </div>
    </ByModal>
  );
};

export default ReserveDetailModal;
